import { FormRow, InputContainer } from '@creditornot/cb-form';
import { colors, cssThemedValue, typographyCss } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { Select } from '@creditornot/cb-select';
import React, { useCallback } from 'react';
import { Field, useField } from 'react-final-form';
import { FieldArray, useFieldArray } from 'react-final-form-arrays';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { KybSubmissionDocument } from 'apis/legitimizer/types';
import { RFFFieldErrorWrapper } from 'common/components';
import { useMerchantContext } from 'modules/merchant/MerchantContext';

import { bankAccountTypesForCountry } from '../countryCodes';
import { DocumentUploader, DocumentViewer } from '../documents';
import { BankDetailsFormValues, BankDetailsFormFieldsConfig, DocumentUpload } from '../types';
import { createEmptyDocumentUpload } from '../utils';

type Props = {
  countryCode: string;
  disabled: boolean;
  config: {
    bankDetails: BankDetailsFormFieldsConfig;
  };
  existingDocuments: KybSubmissionDocument[];
  setDocumentIdList: React.Dispatch<React.SetStateAction<number[]>>;
};

export const StyledFieldset = styled.fieldset`
  padding: 0;
  border: 0;
  margin: 0;
  min-width: 0;
`;
const DocumentWrapper = styled.div`
  padding-block-start: 24px;
  padding-block-end: 24px;
`;

const DocumentsContainer = styled.div`
  & > ${DocumentWrapper} {
    border-block-start: 1px solid ${cssThemedValue('borderPrimary')};
  }
`;

const Label = styled.label`
  ${typographyCss.Body3}
  color: ${cssThemedValue('textSecondary')};
`;

const Asterisk = styled.span`
  color: ${colors.strawberry100};
`;

const bankAccountNumberInfoMessageKeyByCountry = {
  ISR: 'compliance.kyb.sections.bank-details.account-number-info.isr',
};

const isBankAccountNumberInfoMessageCountry = (
  country: Country,
): country is keyof typeof bankAccountNumberInfoMessageKeyByCountry => {
  return Object.keys(bankAccountNumberInfoMessageKeyByCountry).includes(country);
};

const getBankAccountNumberInfoMessage = (country: Country) => {
  return isBankAccountNumberInfoMessageCountry(country) ? bankAccountNumberInfoMessageKeyByCountry[country] : undefined;
};

const BankDetails = ({ countryCode, disabled, config, existingDocuments, setDocumentIdList }: Props) => {
  const intl = useIntl();
  const t = useCallback((id: string) => intl.formatMessage({ id }), [intl]);

  const accountTypeField = useField<BankDetailsFormValues['accountType']>('accountType');
  const bankDocumentsFieldArray = useFieldArray('bankDocuments');

  const newDocumentsLength = bankDocumentsFieldArray.fields.length || 0;
  const singleExistingDocumentInView = existingDocuments.length === 1 && newDocumentsLength === 0;
  const singleNewDocumentInView = newDocumentsLength === 1 && existingDocuments.length === 0;

  const handleRemoveNewDocument = (index: number) => {
    if (singleNewDocumentInView) {
      bankDocumentsFieldArray.fields.push(createEmptyDocumentUpload());
    }

    bankDocumentsFieldArray.fields.remove(index);
  };

  const handleRemoveExistingDocument = (removedDocumentId: number) => {
    if (singleExistingDocumentInView) {
      bankDocumentsFieldArray.fields.push(createEmptyDocumentUpload());
    }

    setDocumentIdList(documentIdList => {
      const updatedDocumentIdList = documentIdList.filter(documentId => documentId !== removedDocumentId);

      return updatedDocumentIdList;
    });
  };

  const { merchant } = useMerchantContext();

  const bankAccountNumberInfoMessage = getBankAccountNumberInfoMessage(merchant.country);

  return (
    <StyledFieldset>
      {config.bankDetails.accountType.visible && (
        <FormRow
          required={config.bankDetails.accountType.required}
          label={t('compliance.kyb.sections.bank-details.account-type')}
          labelHtmlFor="accountType"
        >
          <Field<BankDetailsFormValues['accountType']> name="accountType">
            {field => (
              <InputContainer>
                <RFFFieldErrorWrapper field={field}>
                  {input => (
                    <Select
                      {...input}
                      disabled={disabled || bankAccountTypesForCountry(countryCode).length === 1}
                      options={bankAccountTypesForCountry(countryCode)}
                      inputId="accountType"
                    />
                  )}
                </RFFFieldErrorWrapper>
              </InputContainer>
            )}
          </Field>
        </FormRow>
      )}
      {config.bankDetails.accountOwner.visible && (
        <FormRow
          required={config.bankDetails.accountOwner.required}
          label={t('compliance.kyb.sections.bank-details.account-owner')}
          labelHtmlFor="accountOwner"
          info={t('compliance.kyb.sections.bank-details.account-owner-info')}
        >
          <Field<BankDetailsFormValues['accountOwner']> name="accountOwner">
            {field => (
              <InputContainer>
                <RFFFieldErrorWrapper field={field}>
                  {input => <Input {...input} disabled={disabled} id="accountOwner" />}
                </RFFFieldErrorWrapper>
              </InputContainer>
            )}
          </Field>
        </FormRow>
      )}
      {config.bankDetails.bankIdentifier.visible && accountTypeField.input.value === 'IBAN' && (
        <FormRow
          required={config.bankDetails.bankIdentifier.required}
          label={t('compliance.kyb.sections.bank-details.bank-identifier')}
          labelHtmlFor="bankIdentifier"
          info={t('compliance.kyb.sections.bank-details.bank-identifier-info')}
        >
          <Field<BankDetailsFormValues['bankIdentifier']> name="bankIdentifier">
            {field => (
              <InputContainer>
                <RFFFieldErrorWrapper field={field}>
                  {input => <Input {...input} disabled={disabled} id="bankIdentifier" />}
                </RFFFieldErrorWrapper>
              </InputContainer>
            )}
          </Field>
        </FormRow>
      )}
      {config.bankDetails.accountNumber.visible && (
        <FormRow
          required={config.bankDetails.accountNumber.required}
          label={`${t('compliance.kyb.sections.bank-details.account-number')} (${accountTypeField.input.value})`}
          labelHtmlFor="accountNumber"
          info={bankAccountNumberInfoMessage ? t(bankAccountNumberInfoMessage) : undefined}
        >
          <Field<BankDetailsFormValues['accountNumber']> name="accountNumber">
            {field => (
              <InputContainer>
                <RFFFieldErrorWrapper field={field}>
                  {input => <Input {...input} disabled={disabled} id="accountNumber" />}
                </RFFFieldErrorWrapper>
              </InputContainer>
            )}
          </Field>
        </FormRow>
      )}
      {config.bankDetails.payoutRecipients.visible && (
        <FormRow
          required={config.bankDetails.payoutRecipients.required}
          label={t('compliance.kyb.sections.bank-details.payout-report-emails')}
          labelHtmlFor="payoutRecipients"
          info={t('compliance.kyb.sections.bank-details.tooltip-comma-separated-list')}
        >
          <Field<BankDetailsFormValues['payoutRecipients']> name="payoutRecipients">
            {field => (
              <InputContainer>
                <RFFFieldErrorWrapper field={field}>
                  {input => <Input {...input} disabled={disabled} id="payoutRecipients" />}
                </RFFFieldErrorWrapper>
              </InputContainer>
            )}
          </Field>
        </FormRow>
      )}
      {config.bankDetails.bankDocuments.visible && (
        <DocumentsContainer>
          {existingDocuments.map(document => (
            <DocumentWrapper key={document.id}>
              <Label>
                <FormattedMessage id="compliance.kyb.sections.bank-details.proof-of-bank-account" />{' '}
                {config.bankDetails.bankDocuments.required && <Asterisk>*</Asterisk>}
              </Label>

              <DocumentViewer document={document} onRemove={handleRemoveExistingDocument} />
            </DocumentWrapper>
          ))}

          <FieldArray<DocumentUpload> name="bankDocuments">
            {({ fields }) => (
              <>
                {fields.map((name: string, index: number) => (
                  <Field<File> name={`${name}.file`} key={fields.value[index].tempId}>
                    {field => (
                      <DocumentWrapper>
                        <RFFFieldErrorWrapper field={field}>
                          {input => (
                            <DocumentUploader
                              label={
                                <Label htmlFor={`${name}.file`}>
                                  <FormattedMessage id="compliance.kyb.sections.bank-details.proof-of-bank-account" />{' '}
                                  {config.bankDetails.bankDocuments.required && <Asterisk>*</Asterisk>}
                                </Label>
                              }
                              inputProps={{
                                id: `${name}.file`,
                                name: `${name}.file`,
                              }}
                              onChange={input.onChange}
                              onRemove={() => handleRemoveNewDocument(index)}
                              hideRemove={!input.value && singleNewDocumentInView}
                              info={t('compliance.kyb.sections.bank-details.proof-of-bank-account-info')}
                            />
                          )}
                        </RFFFieldErrorWrapper>
                      </DocumentWrapper>
                    )}
                  </Field>
                ))}
              </>
            )}
          </FieldArray>
        </DocumentsContainer>
      )}
    </StyledFieldset>
  );
};

export default BankDetails;
